import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Trip } from '../lib/types';

interface UseTripsOptions {
  status?: Trip['status'] | 'all';
  driverId?: string;
  dateFrom?: string;
  dateTo?: string;
  platform?: 'all' | 'direkt';
  page?: number;
  perPage?: number;
}

interface UseTripsResult {
  trips: Trip[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useTrips(options: UseTripsOptions = {}): UseTripsResult {
  const { status = 'all', driverId, dateFrom, dateTo, platform = 'all', page = 1, perPage = 20 } = options;
  const [trips, setTrips] = useState<Trip[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    async function fetchTrips() {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('trips')
        .select('*, driver:drivers(id, full_name, phone)', { count: 'exact' });

      if (status !== 'all') query = query.eq('status', status);
      if (driverId) query = query.eq('driver_id', driverId);
      if (dateFrom) query = query.gte('created_at', dateFrom);
      if (dateTo) query = query.lte('created_at', dateTo);
      if (platform === 'direkt') {
        query = query.in('platform', ['Direkt', 'direct', 'gatuhyra', 'Vilotid']);
      }

      query = query
        .order('created_at', { ascending: false })
        .range((page - 1) * perPage, page * perPage - 1);

      const { data, count, error: err } = await query;

      if (err) {
        setError(err.message);
        setTrips([]);
      } else {
        setTrips((data as Trip[]) ?? []);
        setTotal(count ?? 0);
      }
      setLoading(false);
    }

    fetchTrips();
  }, [status, driverId, dateFrom, dateTo, platform, page, perPage, tick]);

  // Live updates: trips created/completed on the mobile app appear without refresh
  useEffect(() => {
    const channel = supabase
      .channel('trips-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'trips' },
        () => setTick((t) => t + 1)
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return { trips, total, loading, error, refetch: () => setTick((t) => t + 1) };
}
